import React, { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { imageUrl } from "../lib/api";

const MAX_ZOOM = 4;
const ZOOM_STEP = 2;

export default function Lightbox({ images = [], startIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const touchStartX = useRef(null);
  const dragRef = useRef(null);
  const lastTapRef = useRef(0);

  useEffect(() => {
    if (open) {
      setIndex(startIndex);
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, startIndex]);

  // Reset zoom when switching photo
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [index]);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  const resetZoom = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Toggle 1× ↔ ZOOM_STEP centered on the click point inside the photo.
  // The image rect + click coords are captured SYNCHRONOUSLY here because
  // React's SyntheticEvent gets nullified before the `setScale` updater
  // runs asynchronously — accessing `e.currentTarget` inside the updater
  // throws "Cannot read properties of null".
  const toggleZoom = useCallback((e) => {
    const rect = e.currentTarget?.getBoundingClientRect?.();
    const clientX = e.clientX;
    const clientY = e.clientY;
    setScale((s) => {
      if (s > 1) {
        setOffset({ x: 0, y: 0 });
        return 1;
      }
      if (!rect) return s;
      const cx = clientX - rect.left - rect.width / 2;
      const cy = clientY - rect.top - rect.height / 2;
      setOffset({ x: -cx * (ZOOM_STEP - 1), y: -cy * (ZOOM_STEP - 1) });
      return ZOOM_STEP;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (scale > 1) resetZoom();
        else onClose();
      } else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX_ZOOM, s + 0.5));
      else if (e.key === "-" || e.key === "_") {
        setScale((s) => {
          const ns = Math.max(1, s - 0.5);
          if (ns === 1) setOffset({ x: 0, y: 0 });
          return ns;
        });
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, prev, next, onClose, scale, resetZoom]);

  // Block native page scroll so wheel events are ours to consume.
  // We attach the wheel handler via a non-passive ref so preventDefault() works.
  const stageRef = useRef(null);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const delta = -Math.sign(e.deltaY) * 0.25;
      setScale((s) => {
        const ns = Math.min(MAX_ZOOM, Math.max(1, s + delta));
        if (ns === 1) setOffset({ x: 0, y: 0 });
        return ns;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  if (!open || images.length === 0) return null;

  const onTouchStart = (e) => {
    if (scale > 1) return; // panning is handled by drag handlers below
    touchStartX.current = e.touches[0].clientX;
    // double-tap to zoom on mobile
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      toggleZoom({
        currentTarget: e.currentTarget,
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
      });
    }
    lastTapRef.current = now;
  };
  const onTouchEnd = (e) => {
    if (scale > 1 || touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 25) (dx > 0 ? prev : next)();
    touchStartX.current = null;
  };

  // Drag-to-pan (mouse + touch) when zoomed
  const startDrag = (clientX, clientY) => {
    if (scale <= 1) return;
    dragRef.current = { startX: clientX, startY: clientY, baseX: offset.x, baseY: offset.y };
  };
  const onDragMove = (clientX, clientY) => {
    if (!dragRef.current) return;
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    setOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  // Suppress click-to-toggle when the click was actually the end of a drag
  const onImgClick = (e) => {
    if (dragRef.current) return;
    toggleZoom(e);
  };

  // Preload neighbouring images so next/prev feels instant.
  const preload = [];
  if (images.length > 1) {
    preload.push((index + 1) % images.length);
    preload.push((index - 1 + images.length) % images.length);
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 text-white/80 text-sm">
        <span className="tracking-wider">
          {index + 1} / {images.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale((s) => {
              const ns = Math.max(1, s - 0.5);
              if (ns === 1) setOffset({ x: 0, y: 0 });
              return ns;
            })}
            disabled={scale <= 1}
            data-testid="lightbox-zoom-out"
            aria-label="Uitzoomen"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="tabular-nums text-xs text-white/60 w-10 text-center" data-testid="lightbox-zoom-level">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale((s) => Math.min(MAX_ZOOM, s + 0.5))}
            disabled={scale >= MAX_ZOOM}
            data-testid="lightbox-zoom-in"
            aria-label="Inzoomen"
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => { if (scale > 1) resetZoom(); else onClose(); }}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors ml-2"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={stageRef}
        className="relative flex-1 min-h-0 flex items-center justify-center px-4 md:px-16 overflow-hidden"
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onMouseMove={(e) => onDragMove(e.clientX, e.clientY)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <button
          onClick={prev}
          className={`absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-[#c5a76f] hover:text-black text-white flex items-center justify-center transition-colors z-10 ${scale > 1 ? "opacity-40" : ""}`}
          aria-label="Vorige"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <img
          src={imageUrl(images[index])}
          alt={`Foto ${index + 1}`}
          onClick={onImgClick}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: dragRef.current ? "none" : "transform 200ms ease-out",
            cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
          }}
          className="block max-w-full max-h-full w-auto h-auto object-contain select-none will-change-transform"
          draggable="false"
          decoding="async"
          fetchPriority="high"
        />
        {/* Hidden neighbour preload — keeps prev/next near-instant. */}
        {preload.map((i) => (
          <img
            key={`preload-${i}`}
            src={imageUrl(images[i])}
            alt=""
            aria-hidden="true"
            className="hidden"
            decoding="async"
          />
        ))}
        <button
          onClick={next}
          className={`absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/5 hover:bg-[#c5a76f] hover:text-black text-white flex items-center justify-center transition-colors z-10 ${scale > 1 ? "opacity-40" : ""}`}
          aria-label="Volgende"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <div className="flex-shrink-0 px-5 pb-5 pt-2">
        <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`flex-shrink-0 w-20 h-14 rounded overflow-hidden border-2 transition-colors ${
                i === index ? "border-[#c5a76f]" : "border-transparent opacity-50 hover:opacity-100"
              }`}
            >
              <img src={imageUrl(src, { thumb: true })} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function GalleryGrid({ images, onOpen }) {
  if (!images || images.length === 0) return null;
  const main = images[0];
  const rest = images.slice(1, 5);

  // Single image: render full-width
  if (images.length === 1) {
    return (
      <button
        onClick={() => onOpen(0)}
        className="group relative w-full overflow-hidden rounded-md bg-[#242424] aspect-[16/9] md:aspect-[21/9]"
      >
        <img src={imageUrl(main)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      </button>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 md:h-[560px]">
      <button
        onClick={() => onOpen(0)}
        className="group relative md:col-span-2 md:row-span-2 overflow-hidden rounded-md bg-[#242424] aspect-[16/10] md:aspect-auto"
      >
        <img src={imageUrl(main)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      </button>
      {rest.map((src, i) => (
        <button
          key={i}
          onClick={() => onOpen(i + 1)}
          className="group relative overflow-hidden rounded-md bg-[#242424] aspect-[4/3] md:aspect-auto hidden md:block"
        >
          <img src={imageUrl(src, { thumb: true })} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          {i === 3 && images.length > 5 && (
            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white">
              <Maximize2 className="w-6 h-6 mb-1 text-[#c5a76f]" />
              <span className="text-sm font-semibold">+{images.length - 5} foto's</span>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}
